import React, { useEffect, useMemo, useState } from 'react';
import { unitLabel } from './unitLabel';
import { ChevronDown, ChevronRight } from 'lucide-react';
import {
  AED_PER_EUR, EUR_LADDER, RENT_LADDER, NEW_CAPITAL_YIELD, YIELD_STEP, YIELD_CEILING,
  Property, Milestone, InstallmentPlan, PortfolioRow, LeaseInfo,
  buildRows, useLeases, nextLadder, eur, fmtEUR, compactEUR, compactAED, fmtAEDFull,
  pct, trimZeros, dateLabel, rateLabel, monthName,
  SoldRow, buildSoldRows, totalRealised
} from './portfolioMetrics';
import { useLanguage } from './LanguageContext';
import './PortfolioReport.css';
import { appLanguage, appLocale } from '../i18n/locale';

/* ================================================================
   PORTFOLIO REPORT — editorial "Investment Portfolio Report" view
   Rendered as the Report tab inside PortfolioView (Portfolio tab).

   Everything is derived from real investor data:
     - units            (props.properties)
     - DLD valuations   (props.dldData, fetched by PortfolioView)
     - payment plans    (/api/installment-plans/:investorId)
     - signed leases    (/pm/owner/:investorId/units — Property Management module)
   Targets are derived from a milestone ladder unless passed in.
   ================================================================ */

// ---------------------------------------------------------------- local config
const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'];

export interface PipelineDeal {
  name: string;
  detail?: string;
  segment?: string;
  phase?: string;
  offerAED: number;
  netRentAED?: number;
  netReturn?: number;
  deadline?: string;
  nextSteps?: string;
}

interface PortfolioReportProps {
  properties: Property[];
  investorName?: string;
  investorId?: string;
  apiUrl?: string;
  dldData?: any;
  /** Optional deal flow for section V — section is hidden when empty. */
  pipeline?: PipelineDeal[];
  /** Optional explicit goals in EUR, e.g. { capital: 100e6, annualNetRent: 10e6 }. */
  targets?: { capital?: number; annualNetRent?: number };
  /** Optional closing line under the pipeline table. */
  deploymentPlan?: string;
  /**
   * Sample leases for the code-gated showcase (portals/demo). Rent is taken
   * only from signed Property Management leases, and PM is unreachable in demo
   * mode — without this the report reads "no rented units yet" beside a unit
   * marked Occupied.
   */
  leasesOverride?: Record<string, LeaseInfo>;
  /** Sample payment plans for the showcase — same reason as leasesOverride. */
  plansOverride?: InstallmentPlan[];
}

// ---------------------------------------------------------------- small pieces
const SectionHead: React.FC<{ index: number; title: string }> = ({ index, title }) => (
  <div className="pr-sec-head">
    <span className="pr-sec-num pr-serif">{ROMAN[index]}.</span>
    <h2 className="pr-sec-title pr-serif">{title}</h2>
    <span className="pr-sec-line" />
  </div>
);

const Bar: React.FC<{ ratio: number; gold?: boolean }> = ({ ratio, gold }) => (
  <div className="pr-bar">
    <span
      className={`pr-bar-fill${gold ? ' pr-fill-gold' : ''}`}
      style={{ width: `${Math.max(0, Math.min(100, ratio * 100))}%` }}
    />
  </div>
);

const Cell: React.FC<{ label: string; children: React.ReactNode; num?: boolean; cls?: string }> = ({ label, children, num, cls }) => (
  <div className={`pr-c${num ? ' pr-num' : ''}${cls ? ` ${cls}` : ''}`}>
    <span className="pr-cell-lab">{label}</span>
    {children}
  </div>
);

// ================================================================ component
const PortfolioReport: React.FC<PortfolioReportProps> = ({
  properties,
  investorName,
  investorId,
  apiUrl,
  dldData,
  pipeline,
  targets,
  deploymentPlan,
  leasesOverride,
  plansOverride
}) => {
  const { t } = useLanguage();
  const units = useMemo(() => properties || [], [properties]);
  const [plans, setPlans] = useState<InstallmentPlan[]>([]);
  const [openUnit, setOpenUnit] = useState<string | null>(null);
  const [openYear, setOpenYear] = useState<number | null>(null);
  const [primary, setPrimary] = useState<'EUR' | 'AED'>('EUR');
  // The owner's own annual net rent target, in EUR. null = never set, so the
  // ladder default applies. Kept per investor so two accounts on one device do
  // not inherit each other's goal.
  const targetKey = `investmenterRentTarget:${investorId || 'self'}`;
  const [ownTarget, setOwnTarget] = useState<number | null>(() => {
    try {
      const raw = localStorage.getItem(`investmenterRentTarget:${investorId || 'self'}`);
      const n = raw ? Number(raw) : NaN;
      return isFinite(n) && n > 0 ? n : null;
    } catch { return null; }
  });
  const [editTarget, setEditTarget] = useState(false);
  const [targetDraft, setTargetDraft] = useState('');

  const saveTarget = () => {
    const n = Number(String(targetDraft).replace(/[^\d]/g, ''));
    if (!isFinite(n) || n <= 0) { setEditTarget(false); return; }
    setOwnTarget(n);
    try { localStorage.setItem(targetKey, String(n)); } catch { /* private mode */ }
    setEditTarget(false);
  };

  const clearTarget = () => {
    setOwnTarget(null);
    try { localStorage.removeItem(targetKey); } catch { /* private mode */ }
    setEditTarget(false);
  };
  const [tick, setTick] = useState(Date.now());

  // live "earned today" ticker
  useEffect(() => {
    const id = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // payment plans
  useEffect(() => {
    if (plansOverride) { setPlans(plansOverride); return; }
    if (!investorId || !apiUrl) return;
    const token = localStorage.getItem('authToken');
    // PortfolioView receives apiUrl already ending in /api (InvestorPortal appends it),
    // but other callers pass the bare service URL — normalise so we never double it up.
    const base = apiUrl.replace(/\/api\/?$/, '');
    fetch(`${base}/api/installment-plans/${encodeURIComponent(investorId)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
      .then(r => (r.ok ? r.json() : []))
      .then(data => setPlans(Array.isArray(data) ? data : []))
      .catch(() => setPlans([]));
  }, [investorId, apiUrl, plansOverride]);

  const leases = useLeases(investorId, leasesOverride);

  // ------------------------------------------------------------- portfolio maths
  const rows: PortfolioRow[] = useMemo(
    () => buildRows(units, dldData, leases),
    [units, dldData, leases]
  );

  // Sold units are not holdings — buildRows drops them, so every active total
  // below excludes them and their money is reported here instead, as realised
  // proceeds and a realised return.
  const soldRows: SoldRow[] = useMemo(() => buildSoldRows(units), [units]);
  const realised = useMemo(() => totalRealised(soldRows), [soldRows]);

  const totalPurchase = rows.reduce((s, r) => s + r.purchase, 0);
  const rentedRows = rows.filter(r => r.annualRent > 0);
  const annualNetRent = rentedRows.reduce((s, r) => s + r.annualRent, 0);
  // units whose rent comes from a signed lease in Property Management
  const leaseRows = rows.filter(r => (r.lease?.annualRent || 0) > 0);
  const completedRows = rows.filter(r => !r.offplan);
  const offplanRows = rows.filter(r => r.offplan);
  const completedValue = completedRows.reduce((s, r) => s + r.purchase, 0);
  const offplanValue = offplanRows.reduce((s, r) => s + r.purchase, 0);

  const capitalTarget = targets?.capital || nextLadder(eur(totalPurchase), EUR_LADDER);
  // Owner's own figure wins, then anything passed in, then the ladder.
  const rentTarget = ownTarget || targets?.annualNetRent || nextLadder(eur(annualNetRent), RENT_LADDER);
  const capitalRatio = capitalTarget > 0 ? eur(totalPurchase) / capitalTarget : 0;
  const rentRatio = rentTarget > 0 ? eur(annualNetRent) / rentTarget : 0;

  // live earnings
  const now = new Date(tick);
  const dayFraction =
    (now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()) / 86400;
  const dailyRentEUR = eur(annualNetRent) / 365;
  const earnedTodayEUR = dailyRentEUR * dayFraction;

  // ------------------------------------------------------------- payments
  const payments = useMemo(() => {
    const all: Array<Milestone & { unitName: string; unitNumber: string; project: string }> = [];
    plans.forEach(plan => (plan.milestones || []).forEach(m => all.push({
      ...m,
      unitName: plan.unitName || plan.project || (appLanguage() === 'de' ? 'Einheit' : 'Unit'),
      unitNumber: plan.unitNumber || '',
      project: plan.project || ''
    })));

    const byYear = new Map<number, typeof all>();
    all.forEach(m => {
      const year = new Date(m.dueDate).getFullYear();
      if (isNaN(year)) return;
      byYear.set(year, [...(byYear.get(year) || []), m]);
    });

    const years = [...byYear.entries()]
      .map(([year, items]) => {
        const total = items.reduce((s, m) => s + (m.amount || 0), 0);
        const paid = items.filter(m => m.status === 'paid').reduce((s, m) => s + (m.amount || 0), 0);
        return {
          year,
          count: items.length,
          total,
          paid,
          open: total - paid,
          items: items.slice().sort((a, b) => a.dueDate.localeCompare(b.dueDate))
        };
      })
      .sort((a, b) => a.year - b.year);

    const upcoming = all
      .filter(m => m.status !== 'paid' && new Date(m.dueDate).getTime() >= Date.now() - 86400e3 * 7)
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate));

    return {
      years,
      upcoming,
      total: all.reduce((s, m) => s + (m.amount || 0), 0),
      paid: all.filter(m => m.status === 'paid').reduce((s, m) => s + (m.amount || 0), 0),
      count: all.length,
      hasData: all.length > 0
    };
  }, [plans]);

  const paidAED = payments.hasData ? payments.paid : 0;
  const openAED = payments.hasData ? payments.total - payments.paid : 0;
  const divisionBase = payments.hasData ? payments.total : totalPurchase;

  // ------------------------------------------------------------- projections
  const projection = useMemo(() => {
    const year = new Date().getFullYear();
    const startMonth = new Date().getMonth();

    // Rent is based ONLY on ACTUAL signed leases in Property Management.
    // `annualNetRent` is the sum of each unit's real lease rent (r.annualRent),
    // which is already 0 for any unit without a lease. No DLD market-rent or
    // yield estimates are added — an unleased or off-plan unit contributes 0.
    // Only the invested-capital column still phases in from your payment plans.
    let cumulative = totalPurchase;
    const months: Array<{
      label: string; inflow: number; cumulative: number; annualRent: number; monthlyRent: number;
    }> = [];
    for (let m = startMonth; m < 12; m++) {
      const inflow = payments.upcoming
        .filter(p => {
          const d = new Date(p.dueDate);
          return d.getFullYear() === year && d.getMonth() === m;
        })
        .reduce((s, p) => s + (p.amount || 0), 0);
      if (m > startMonth || inflow > 0) cumulative += inflow;
      months.push({ label: monthName(m), inflow, cumulative, annualRent: annualNetRent, monthlyRent: annualNetRent / 12 });
    }
    const peak = months[months.length - 1] || {
      label: monthName(startMonth),
      inflow: 0,
      cumulative: totalPurchase,
      annualRent: annualNetRent,
      monthlyRent: annualNetRent / 12
    };
    return { year, months, peak, maxRent: Math.max(...months.map(m => m.annualRent), 1) };
  }, [payments.upcoming, totalPurchase, annualNetRent]);

  const longTerm = useMemo(() => {
    const basis = Math.max(eur(projection.peak.cumulative), capitalTarget * capitalRatio);
    const portfolio = Math.max(basis, capitalTarget);
    const startYear = new Date().getFullYear() + 1;
    const yearRows: Array<{ year: number; portfolio: number; yieldPct: number; annual: number; monthly: number; goal: boolean }> = [];
    let y = NEW_CAPITAL_YIELD;
    let reached = false;
    for (let i = 0; i < 8 && !reached; i++) {
      const annual = portfolio * y;
      reached = annual >= rentTarget - 1;
      yearRows.push({
        year: startYear + i,
        portfolio,
        yieldPct: y * 100,
        annual,
        monthly: annual / 12,
        goal: reached
      });
      y = Math.min(YIELD_CEILING, y + YIELD_STEP);
      if (y >= YIELD_CEILING && portfolio * y < rentTarget && i > 5) break;
    }
    return { rows: yearRows, maxAnnual: Math.max(...yearRows.map(r => r.annual), rentTarget) };
  }, [projection.peak.cumulative, capitalTarget, capitalRatio, rentTarget]);

  // ------------------------------------------------------------- currency display (EUR-denominated)
  // Targets, ladders and the live ticker are held in EUR. These follow the
  // EUR/AED toggle so switching to AED converts every figure, not just the
  // tiles that happen to start from an AED amount.
  const moneyE = (e: number) => (primary === 'EUR' ? compactEUR(e) : compactAED(e * AED_PER_EUR));
  const moneyAltE = (e: number) => (primary === 'EUR' ? compactAED(e * AED_PER_EUR) : compactEUR(e));
  const liveMoney = (e: number) =>
    primary === 'EUR'
      ? fmtEUR(e, 2)
      : `AED ${(e * AED_PER_EUR).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  // ------------------------------------------------------------- achievements
  const badges = useMemo(() => {
    const top = rows[0];
    const commercial = rows.find(r =>
      /office|retail|commercial|villa/i.test(`${r.unit.type || ''} ${r.unit.name || ''}`));
    const capEUR = eur(totalPurchase);
    const firstCapStep = EUR_LADDER.filter(s => s <= capEUR).pop() || EUR_LADDER[0];
    const nextRentStep = RENT_LADDER.find(s => s > eur(annualNetRent)) || rentTarget;
    return [
      {
        done: !!top, icon: '◆', name: t('prBadgeTrophy'),
        desc: top ? `${top.unit.name || top.unit.project} ${t('prSecuredSuffix')}` : t('prBadgeLargestUnit')
      },
      {
        done: rows.length > 0, icon: String(rows.length || '–'), name: t('prBadgePortfolioBasis'),
        desc: `${rows.length} ${rows.length === 1 ? t('prUnit') : t('prUnits')} ${t('prAcquired')}`
      },
      {
        done: !!commercial, icon: 'C1', name: t('prBadgeCommercial'),
        desc: commercial ? `${commercial.unit.name || commercial.unit.project}` : t('prBadgeOfficeVilla')
      },
      {
        done: capEUR >= firstCapStep, icon: moneyE(firstCapStep), name: `${t('prFirst')} ${moneyE(firstCapStep)}`,
        desc: `${moneyE(capEUR)} ${t('prDeployed')}`
      },
      {
        done: rentedRows.length > 0, icon: 'R1', name: t('prBadgeFirstRent'),
        desc: `${rentedRows.length} ${rentedRows.length === 1 ? t('prPropertySing') : t('prPropertyPlur')} ${t('prRentedSuffix')}`
      },
      {
        done: eur(annualNetRent) >= nextRentStep, icon: moneyE(nextRentStep), name: `${t('prRentTarget')} ${moneyE(nextRentStep)}`,
        desc: `${moneyE(nextRentStep)} ${t('prAnnualNetRentLower')}`
      },
      {
        done: capitalRatio >= 0.5, icon: '½', name: t('prBadgeHalfTime'),
        desc: `${moneyE(capitalTarget / 2)} ${t('prDeployed')}`
      },
      {
        done: capitalRatio >= 1, icon: '★', name: `${t('prMission')} ${moneyE(capitalTarget).replace('$', '')}`,
        desc: `${moneyE(capitalTarget)} ${t('prTargetReached')}`
      }
    ];
  }, [rows, totalPurchase, annualNetRent, rentedRows.length, capitalRatio, capitalTarget, rentTarget, t, primary]);

  const unlocked = badges.filter(b => b.done).length;

  // ------------------------------------------------------------- currency display
  const money = (aed: number) => (primary === 'EUR' ? compactEUR(eur(aed)) : compactAED(aed));
  const moneyAlt = (aed: number) => (primary === 'EUR' ? compactAED(aed) : compactEUR(eur(aed)));
  const moneyFull = (aed: number) =>
    primary === 'EUR' ? fmtEUR(Math.round(eur(aed))) : `AED ${fmtAEDFull(aed)}`;
  // The ALT figure carries its currency code. Without it the register showed
  // a bare "1,630,888" beside "EUR 407.722" and nothing on the row said what
  // the second number was — read as a second price rather than the same price
  // in dirhams.
  const moneyAltFull = (aed: number) =>
    primary === 'EUR' ? `AED ${fmtAEDFull(aed)}` : fmtEUR(Math.round(eur(aed)));

  /**
   * The register's row title.
   *
   * Unit names arrive from several sources and many already embed the project
   * AND the unit number — "SOBHA ONE - Sobha One - D/D1702" — while the row
   * shows both of those in its own Unit and Position columns. The result was
   * the same string three times in one row, which is what made the list hard
   * to read. So strip from the title whatever the row already states
   * elsewhere, and fall back to the project when nothing is left.
   */
  const registerTitle = (u: any): string => {
    const raw = String(u?.name || '').trim();
    const project = String(u?.project || '').trim();
    const unitNo = String(u?.unitNumber || '').trim();
    if (!raw) return project || unitNo || t('prProperty');

    // ONLY the composite unit code comes out. Stripping the project as well
    // looked tidier on the worst rows and wrecked the good ones — "Seagate
    // Building 2" became "Building 2", "Marina Gate (Tower 2) + Annex" became
    // "+ Annex". A project name is usually a legitimate part of the name.
    //
    // The 5-character floor is what separates a composite code
    // ("N1-4204", "Sobha One - D/D1702") from a plain unit number ("1204",
    // "510"), which appears inside honest names like "Building 510" and must
    // be left alone.
    let out = raw;
    if (unitNo.length >= 5) {
      // Escape first: a unit code with a bracket or a plus in it would
      // otherwise compile to an invalid pattern and throw, taking the whole
      // report down with it.
      const esc = unitNo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      out = out.replace(new RegExp(esc, 'ig'), ' ');
    }
    // whatever separators the removal left behind
    out = out.replace(/\s{2,}/g, ' ').replace(/^[\s\-–·|,/]+|[\s\-–·|,/]+$/g, '').trim();
    return out || project || unitNo || t('prProperty');
  };

  // Roman numerals are handed out in render order, so a section that is hidden
  // — no payment plans, no pipeline, nothing sold yet — does not leave a hole
  // in the numbering or collide with the section after it.
  const secCounter = { n: 0 };
  const nextSec = () => secCounter.n++;

  const firstName = (investorName || '').trim().split(' ')[0];
  const reportDate = new Date().toLocaleDateString(appLocale(), { day: 'numeric', month: 'long', year: 'numeric' });

  /* ================ Realised sales ================
    The sold-unit report: what each unit was bought for, what it sold
    for, what the transfer cost, and the return that was actually
    realised. Hidden until the first sale is recorded. */
  const renderRealisedSales = () => soldRows.length === 0 ? null : (
    <section className="pr-sec">
      <SectionHead index={nextSec()} title={t('prSecSales')} />
      <p className="pr-lede pr-serif">
        {soldRows.length} {soldRows.length === 1 ? t('prUnit') : t('prUnits')} {t('prSoldLede')}{' '}
        {realised.netProfit >= 0 ? '' : '−'}{money(Math.abs(realised.netProfit))} {t('prRealisedProfitLower')}.
      </p>
      {realised.assignments > 0 && (
        <p className="pr-hint" style={{ margin: '0 2px 14px' }}>
          {realised.assignments} {realised.assignments === 1 ? t('prOfWhichAssignmentOne') : t('prOfWhichAssignmentMany')}
        </p>
      )}

      <div className="pr-kpis">
        <div className="pr-kpi">
          <p className="pr-label">{t('prSaleProceedsGross')}</p>
          <p className="pr-kpi-val pr-serif">{money(realised.salePrice)}</p>
          <p className="pr-kpi-alt pr-serif">{moneyAlt(realised.salePrice)}</p>
          <p className="pr-kpi-note">
            {soldRows.length} {soldRows.length === 1 ? t('prUnitSold') : t('prUnitsSold')}
          </p>
        </div>
        <div className="pr-kpi">
          <p className="pr-label">{t('prSellingCosts')}</p>
          <p className="pr-kpi-val pr-serif">−{money(realised.totalCosts)}</p>
          <p className="pr-kpi-alt pr-serif">{moneyAlt(realised.totalCosts)}</p>
          <p className="pr-kpi-note">
            {realised.salePrice > 0
              ? `${pct((realised.totalCosts / realised.salePrice) * 100)} ${t('prOfSalePrice')}`
              : t('prDldCommissionTrustee')}
          </p>
        </div>
        <div className="pr-kpi">
          <p className="pr-label">{realised.outstandingTransferred > 0 ? t('prCashReceived') : t('prNetProceeds')}</p>
          <p className="pr-kpi-val pr-serif">{money(realised.cashToSeller)}</p>
          <p className="pr-kpi-alt pr-serif">{moneyAlt(realised.cashToSeller)}</p>
          <p className="pr-kpi-note">
            {realised.outstandingTransferred > 0
              ? `${t('prAfterCosts')} · ${money(realised.outstandingTransferred)} ${t('prBalanceToDeveloper')}`
              : t('prAfterCosts')}
          </p>
        </div>
        <div className="pr-kpi">
          <p className="pr-label">{t('prRealisedProfit')}</p>
          <p
            className="pr-kpi-val pr-serif"
            style={{ color: realised.netProfit >= 0 ? 'var(--pr-green)' : 'var(--pr-red)' }}
          >
            {realised.netProfit >= 0 ? '+' : '−'}{money(Math.abs(realised.netProfit))}
          </p>
          <p className="pr-kpi-alt pr-serif">{moneyAlt(Math.abs(realised.netProfit))}</p>
          <p className="pr-kpi-note">
            {realised.netProfit >= 0 ? '+' : ''}{pct(realised.roiPercent)} {t('prRealisedRoiLower')}
          </p>
        </div>
      </div>

      <div className="pr-table">
        <div className="pr-thead pr-grid-sold-h">
          <span>{t('prColObject')}</span><span>{t('prColSold')}</span><span>{t('prColHeld')}</span>
          <span className="pr-num">{t('prColCapitalIn')} ({primary})</span>
          <span className="pr-num">{t('prColSalePrice')} ({primary})</span>
          <span className="pr-num">{t('prColCosts')}</span>
          <span className="pr-num">{t('prColNetProfit')}</span>
          <span className="pr-num">ROI</span>
        </div>
        {soldRows.map(({ unit, sale }) => {
          const isOpen = openUnit === unit.id;
          const won = sale.netProfit >= 0;
          return (
            <React.Fragment key={unit.id}>
              <button
                className="pr-tr pr-grid-sold"
                data-open={isOpen}
                onClick={() => setOpenUnit(isOpen ? null : unit.id)}
              >
                <div className="pr-c-main">
                  <div className="pr-tr-name">
                    {isOpen
                      ? <ChevronDown size={13} color="#a4844a" />
                      : <ChevronRight size={13} color="#a4844a" />}
                    {unit.name || unit.project || t('prProperty')}
                  </div>
                  <div className="pr-tr-sub">
                    {unit.unitNumber ? `${unit.unitNumber} · ` : ''}{unit.project || unit.location || '—'}
                    {sale.saleType === 'assignment' && ` · ${t('prAssignmentLabel')}`}
                  </div>
                </div>
                <Cell label={t('prColSold')}>
                  <div className="pr-tr-sub" style={{ marginTop: 0 }}>{dateLabel(sale.saleDate)}</div>
                </Cell>
                <Cell label={t('prColHeld')}>
                  <div className="pr-tr-sub" style={{ marginTop: 0 }}>
                    {sale.holdingMonths ? `${sale.holdingMonths} ${t('prMonthsShort')}` : '—'}
                  </div>
                </Cell>
                <Cell label={`${t('prColCapitalIn')} (${primary})`} num>
                  <div className="pr-tr-fig pr-serif">
                    {sale.capitalBase > 0 ? moneyFull(sale.capitalBase) : '—'}
                  </div>
                  {sale.saleType === 'assignment' && sale.purchase > 0 && (
                    <div className="pr-tr-fig-alt pr-serif">
                      {t('prOfSpaPrice')} {moneyFull(sale.purchase)}
                    </div>
                  )}
                  {sale.acquisitionPrice > 0 && (
                    <div className="pr-tr-fig-alt">{t('prTakenOverLabel')}</div>
                  )}
                </Cell>
                <Cell label={`${t('prColSalePrice')} (${primary})`} num>
                  <div className="pr-tr-fig pr-serif">{moneyFull(sale.salePrice)}</div>
                  <div className="pr-tr-fig-alt pr-serif">{moneyAltFull(sale.salePrice)}</div>
                </Cell>
                <Cell label={t('prColCosts')} num>
                  <div className="pr-tr-fig-alt pr-serif" style={{ marginTop: 0 }}>
                    {sale.totalCosts > 0 ? `−${moneyFull(sale.totalCosts)}` : '—'}
                  </div>
                </Cell>
                <Cell label={t('prColNetProfit')} num>
                  {sale.hasBasis ? (
                    <div className={`pr-tr-fig pr-serif ${won ? 'pr-green' : 'pr-red'}`}>
                      {won ? '+' : '−'}{moneyFull(Math.abs(sale.netProfit))}
                    </div>
                  ) : (
                    <div className="pr-tr-fig-alt" style={{ marginTop: 0 }}>{t('prNoPurchaseBasis')}</div>
                  )}
                </Cell>
                <Cell label="ROI" num>
                  <div className={`pr-tr-fig pr-serif ${won ? 'pr-green' : 'pr-red'}`}>
                    {sale.hasBasis ? `${won ? '+' : ''}${pct(sale.roiPercent)}` : '—'}
                  </div>
                </Cell>
              </button>

              {isOpen && (
                <div className="pr-detail">
                  <div className="pr-detail-list">
                    <div className="pr-detail-item">
                      <span>{t('prColSalePrice')}</span>
                      <span>{moneyFull(sale.salePrice)}</span>
                    </div>
                    {sale.dldFee > 0 && (
                      <div className="pr-detail-item">
                        <span>{t('prDldTransferFee')}</span>
                        <span>−{moneyFull(sale.dldFee)}</span>
                      </div>
                    )}
                    {sale.commission > 0 && (
                      <div className="pr-detail-item">
                        <span>{t('prAgencyCommission')}</span>
                        <span>−{moneyFull(sale.commission)}</span>
                      </div>
                    )}
                    {sale.trusteeFee > 0 && (
                      <div className="pr-detail-item">
                        <span>{t('prTrusteeFee')}</span>
                        <span>−{moneyFull(sale.trusteeFee)}</span>
                      </div>
                    )}
                    {sale.nocFee > 0 && (
                      <div className="pr-detail-item">
                        <span>{t('prNocFee')}</span>
                        <span>−{moneyFull(sale.nocFee)}</span>
                      </div>
                    )}
                    {sale.developerAdminFee > 0 && (
                      <div className="pr-detail-item">
                        <span>{t('prDeveloperAdminFee')}</span>
                        <span>−{moneyFull(sale.developerAdminFee)}</span>
                      </div>
                    )}
                    {sale.otherExpenses.map((e, i) => (
                      <div className="pr-detail-item" key={`${e.label}-${i}`}>
                        <span>{e.label || t('prOtherExpenses')}</span>
                        <span>−{moneyFull(e.amount)}</span>
                      </div>
                    ))}
                    {/* An assignment hands the developer balance to the buyer,
                        so it never reaches the seller: not a cost, not profit. */}
                    {sale.outstandingBalance > 0 && (
                      <div className="pr-detail-item">
                        <span>{t('prOutstandingAssumed')}</span>
                        <span>−{moneyFull(sale.outstandingBalance)}</span>
                      </div>
                    )}
                    <div className="pr-detail-item">
                      <span><b>{sale.saleType === 'assignment' ? t('prCashReceived') : t('prNetProceeds')}</b></span>
                      <span><b>{moneyFull(sale.cashToSeller)}</b></span>
                    </div>
                    {/* A unit taken over from an earlier owner cost two things:
                        the price paid to that owner and any installments since. */}
                    {sale.acquisitionPrice > 0 && (
                      <div className="pr-detail-item">
                        <span>{t('prPaidPreviousOwner')}</span>
                        <span>−{moneyFull(sale.acquisitionPrice)}</span>
                      </div>
                    )}
                    {sale.acquisitionPrice > 0 && sale.paidToDate > 0 && (
                      <div className="pr-detail-item">
                        <span>{t('prInstallmentsSince')}</span>
                        <span>−{moneyFull(sale.paidToDate)}</span>
                      </div>
                    )}
                    <div className="pr-detail-item">
                      <span>
                        <b>{sale.saleType === 'assignment' ? t('prCapitalPaidIn') : t('prColPurchase')}</b>
                      </span>
                      <span>
                        <b>{sale.hasBasis
                          ? (sale.acquisitionPrice > 0 ? moneyFull(sale.capitalBase) : `−${moneyFull(sale.capitalBase)}`)
                          : t('prNoPurchaseBasis')}</b>
                      </span>
                    </div>
                    <div className="pr-detail-item">
                      <span><b>{t('prRealisedProfit')}</b></span>
                      {sale.hasBasis ? (
                        <span className={won ? 'pr-green' : 'pr-red'}>
                          <b>{won ? '+' : '−'}{moneyFull(Math.abs(sale.netProfit))}</b>
                        </span>
                      ) : (
                        <span>—</span>
                      )}
                    </div>
                  </div>
                  <div className="pr-detail-grid">
                    <div>
                      <p className="pr-label">{t('prGrossProfit')}</p>
                      <p className="pr-detail-val pr-serif">
                        {sale.hasBasis
                          ? `${sale.grossProfit >= 0 ? '+' : '−'}${moneyFull(Math.abs(sale.grossProfit))}`
                          : '—'}
                      </p>
                      <p className="pr-tr-fig-alt">
                        {t('prBeforeCosts')}
                        {sale.hasBasis && sale.entryPrice > 0 &&
                          ` · ${pct(sale.grossRoiPercent)} ${sale.entryPrice !== sale.purchase ? t('prOnEntryPrice') : t('prOnSpaPrice')}`}
                      </p>
                    </div>
                    <div>
                      <p className="pr-label">{t('prAnnualisedRoi')}</p>
                      <p className="pr-detail-val pr-serif">
                        {sale.annualizedRoi != null
                          ? `${sale.annualizedRoi >= 0 ? '+' : ''}${pct(sale.annualizedRoi)}`
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="pr-label">{t('prPurchased')}</p>
                      <p className="pr-detail-val pr-serif">
                        {dateLabel(unit.purchaseDate || unit.otpDate || unit.createdAt)}
                      </p>
                    </div>
                    <div>
                      <p className="pr-label">{t('prSaleDate')}</p>
                      <p className="pr-detail-val pr-serif">{dateLabel(sale.saleDate)}</p>
                    </div>
                  </div>
                  {unit.saleNotes && <p className="pr-hint">{unit.saleNotes}</p>}
                </div>
              )}
            </React.Fragment>
          );
        })}
        <div className="pr-tr pr-grid-sold pr-tr-total">
          <div className="pr-c-main">
            <div className="pr-tr-name">{t('prInTotal')}</div>
          </div>
          <div className="pr-c" /><div className="pr-c" />
          <Cell label={`${t('prColCapitalIn')} (${primary})`} num>
            <div className="pr-tr-fig pr-serif">{moneyFull(realised.invested)}</div>
          </Cell>
          <Cell label={`${t('prColSalePrice')} (${primary})`} num>
            <div className="pr-tr-fig pr-serif">{moneyFull(realised.salePrice)}</div>
          </Cell>
          <Cell label={t('prColCosts')} num>
            <div className="pr-tr-fig-alt pr-serif" style={{ marginTop: 0 }}>−{moneyFull(realised.totalCosts)}</div>
          </Cell>
          <Cell label={t('prColNetProfit')} num>
            <div className={`pr-tr-fig pr-serif ${realised.netProfit >= 0 ? 'pr-green' : 'pr-red'}`}>
              {realised.netProfit >= 0 ? '+' : '−'}{moneyFull(Math.abs(realised.netProfit))}
            </div>
          </Cell>
          <Cell label="ROI" num>
            <div className={`pr-tr-fig pr-serif ${realised.netProfit >= 0 ? 'pr-green' : 'pr-red'}`}>
              {realised.invested > 0 ? `${realised.netProfit >= 0 ? '+' : ''}${pct(realised.roiPercent)}` : '—'}
            </div>
          </Cell>
        </div>
        <p className="pr-foot">
          {t('prSalesFoot')}
          {realised.assignments > 0 && ` ${t('prSalesFootAssignment')}`}
          {realised.unpriced > 0 && ` ${realised.unpriced} ${realised.unpriced === 1 ? t('prSaleNoBasisOne') : t('prSaleNoBasisMany')}`}
        </p>
      </div>
      <p className="pr-hint">▸ {t('prTapSale')}</p>
    </section>
  );

  // Nothing held. That is either a brand-new account or a portfolio that has
  // been sold out — and in the second case the sales report is the only thing
  // left to show, so it must survive the empty state instead of being replaced
  // by "your report appears as soon as the first unit is registered".
  if (rows.length === 0) {
    return (
      <div className="pr-root">
        <p className="pr-kicker">{t('prKicker')}</p>
        <h1 className="pr-title pr-serif">Dubai <em>{t('prTitleEm')}</em></h1>
        <hr className="pr-hr" />
        <div className="pr-panel">
          <p className="pr-empty">
            {soldRows.length > 0 ? t('prEmptyAllSold') : t('prEmpty')}
          </p>
        </div>
        {renderRealisedSales()}
      </div>
    );
  }

  // ================================================================ render
  return (
    <div className="pr-root">
      {/* ---------------- masthead ---------------- */}
      {/* The opening of a financial report, not a brochure cover.
          It used to run eleven lines before the first figure: a kicker, a
          title, a tagline, three stacked meta lines, then a three-line hero
          and the earned-today number. Every one of them was set at a weight
          that asked to be read.

          Now: who it is for and what it is, the date and terms on one line,
          and the only live number on the page — what the portfolio earned
          today — given the room it deserves. The tagline stays as the accent
          line it was written to be, in the italic serif, once. */}
      <div className="pr-masthead">
        <div className="pr-masthead-top">
          <div>
            <p className="pr-kicker">
              {t('prKicker')}{investorName ? ` · ${investorName}` : ''}
            </p>
            <h1 className="pr-title pr-serif">Dubai <em>{t('prTitleEm')}</em></h1>
            <p className="pr-tagline pr-serif">{t('prTagline')}</p>
          </div>
          <div className="pr-meta">
            {/* One line, not three: the date, the rate everything converts at,
                and the confidentiality note read as a single caption. */}
            <span className="pr-meta-row">
              <b>{reportDate}</b> · <b>AED / EUR</b> {AED_PER_EUR.toFixed(2)} · {t('prMetaConfidential')}
            </span>
            <span className="pr-cur-toggle" style={{ marginTop: 8 }}>
              {(['EUR', 'AED'] as const).map(cur => (
                <button key={cur} data-on={primary === cur} onClick={() => setPrimary(cur)}>
                  {cur} {t('prCurFirst')}
                </button>
              ))}
            </span>
          </div>
        </div>
      </div>
      <hr className="pr-hr" />

      {/* ---------------- hero: one line, one live figure ---------------- */}
      {annualNetRent > 0 && (
        <div className="pr-hero">
          <p className="pr-hero-earned">
            <span className="pr-live-dot" />
            {t('prEarnedToday')}
          </p>
          <p className="pr-hero-figure pr-serif">{liveMoney(earnedTodayEUR)}</p>
          <p className="pr-hero-sub">{t('prHeroH')}</p>
        </div>
      )}

      {/* ================ At a glance ================ */}
      <section className="pr-sec">
        <SectionHead index={nextSec()} title={t('prSec1')} />

        <div className="pr-kpis">
          <div className="pr-kpi">
            <p className="pr-label">{t('prTotalInvestment')}</p>
            <p className="pr-kpi-val pr-serif">{money(totalPurchase)}</p>
            <p className="pr-kpi-alt pr-serif">{moneyAlt(totalPurchase)}</p>
            <p className="pr-kpi-note">{rows.length} {t('prPropsAtPurchase')}</p>
          </div>
          <div className="pr-kpi">
            <p className="pr-label">{t('prAnnualNetRent')}</p>
            <p className="pr-kpi-val pr-serif">{money(annualNetRent)}</p>
            <p className="pr-kpi-alt pr-serif">{moneyAlt(annualNetRent)}</p>
            <p className="pr-kpi-note">
              {rentedRows.length > 0
                ? rentedRows.slice(0, 3).map(r => r.unit.name || r.unit.project).join(' · ')
                : t('prNoRentedYet')}
              {leaseRows.length > 0 && (
                <> · {leaseRows.length} {leaseRows.length === 1 ? t('prSignedLease') : t('prSignedLeases')}</>
              )}
            </p>
          </div>
          <div className="pr-kpi">
            <p className="pr-label">{t('prRentTargetProgress')}</p>
            <p className="pr-kpi-val pr-serif pr-gold">
              {(rentRatio * 100).toFixed(1)}<small>%</small>
            </p>
            <p className="pr-kpi-alt pr-serif" style={{ color: 'var(--pr-ink)' }}>
              {money(annualNetRent)} / {moneyE(rentTarget)}
            </p>
            <p className="pr-kpi-note">{t('prSecured')} · {rentedRows.length} {t('prRentedProperties')}</p>
            {/* The control for this figure lives in the rent-target panel
                below, where the target is spelled out in full. */}
          </div>
          <div className="pr-kpi">
            <p className="pr-label">{t('prUnitsInPortfolio')}</p>
            <p className="pr-kpi-val pr-serif">{rows.length}</p>
            {/* the headline here is a count, so the sub-line follows the
                selected primary currency — not the secondary one */}
            <p className="pr-kpi-alt pr-serif">{money(totalPurchase)}</p>
            <p className="pr-kpi-note">
              {completedRows.length} {t('prCompletedLower')} · {offplanRows.length} {t('prOffPlanLower')}
            </p>
          </div>
        </div>

        {/* Income from selling — realised, and deliberately kept out of the
            KPI row above: those four are the state of what is still owned,
            this is money that has already come back. */}
        {soldRows.length > 0 && (
          <div className="pr-panel">
            <div className="pr-panel-head">
              <p className="pr-label">
                {t('prIncomeFromSelling')} · {soldRows.length}{' '}
                {soldRows.length === 1 ? t('prUnitSold') : t('prUnitsSold')}
              </p>
              <span
                className="pr-panel-fig pr-serif"
                style={{ color: realised.netProfit >= 0 ? 'var(--pr-green)' : 'var(--pr-red)' }}
              >
                {realised.netProfit >= 0 ? '+' : ''}{pct(realised.roiPercent)}
              </span>
            </div>
            <div className="pr-detail-grid" style={{ paddingTop: 4 }}>
              <div>
                <p className="pr-label">{t('prRealisedProfit')}</p>
                <p className={`pr-detail-val pr-serif ${realised.netProfit >= 0 ? 'pr-green' : 'pr-red'}`}>
                  {realised.netProfit >= 0 ? '+' : '−'}{moneyFull(Math.abs(realised.netProfit))}
                </p>
                <p className="pr-tr-fig-alt pr-serif">{moneyAltFull(Math.abs(realised.netProfit))}</p>
              </div>
              <div>
                <p className="pr-label">{t('prNetProceeds')}</p>
                <p className="pr-detail-val pr-serif">{moneyFull(realised.netProceeds)}</p>
                <p className="pr-tr-fig-alt pr-serif">{moneyAltFull(realised.netProceeds)}</p>
              </div>
              <div>
                <p className="pr-label">{t('prSellingCosts')}</p>
                <p className="pr-detail-val pr-serif">−{moneyFull(realised.totalCosts)}</p>
                <p className="pr-tr-fig-alt">{t('prDldCommissionTrustee')}</p>
              </div>
              <div>
                <p className="pr-label">{t('prCapitalReturned')}</p>
                <p className="pr-detail-val pr-serif">{moneyFull(realised.invested)}</p>
                <p className="pr-tr-fig-alt">
                  {realised.assignments > 0 ? t('prCapitalAtRisk') : t('prAtPurchasePrice')}
                </p>
              </div>
            </div>
            <div className="pr-note" style={{ marginTop: 14 }}>
              {t('prRealisedNote')}
            </div>
          </div>
        )}

        {/* rent target progress */}
        <div className="pr-panel">
          <div className="pr-panel-head">
            <p className="pr-label">
              {t('prProgressTowardsRent')} <b>{moneyE(rentTarget)} {t('prAnnualNetRentLower')}</b>
            </p>
            <span className="pr-panel-fig pr-serif">{pct(rentRatio * 100)}</span>
          </div>
          <Bar ratio={rentRatio} />
          <div className="pr-scale">
            <span>{moneyE(0)}</span>
            <span>{moneyE(rentTarget * 0.25)}</span>
            <span>{moneyE(rentTarget * 0.5)}</span>
            <span>{moneyE(rentTarget * 0.75)}</span>
            <span>{moneyE(rentTarget)}</span>
          </div>
          <div className="pr-legend">
            <span>
              <i className="pr-dot" style={{ background: 'var(--pr-ink)' }} />
              {t('prSecured')} ({rentedRows.length} {t('prItems')}) — {money(annualNetRent)} · {moneyAlt(annualNetRent)}
            </span>
            <span>
              <i className="pr-dot" style={{ background: 'var(--pr-track)' }} />
              {t('prOpenLegend')} — {moneyE(Math.max(0, rentTarget - eur(annualNetRent)))} ·{' '}
              {moneyAltE(Math.max(0, rentTarget - eur(annualNetRent)))}
            </span>
          </div>
          {/* The target is named in this panel's heading, so the way to change
              it belongs here rather than in the summary tile above. */}
          {editTarget ? (
              <div className="pr-target-edit">
                <label className="pr-label" htmlFor="pr-target-input">{t('prTargetLabel')}</label>
                <div className="pr-target-row">
                  <span aria-hidden="true">&euro;</span>
                  <input
                    id="pr-target-input"
                    className="pr-target-input"
                    type="text"
                    inputMode="numeric"
                    autoFocus
                    value={targetDraft}
                    placeholder={String(Math.round(rentTarget))}
                    onChange={(e) => setTargetDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveTarget();
                      if (e.key === 'Escape') setEditTarget(false);
                    }}
                  />
                </div>
                <div className="pr-target-actions">
                  <button type="button" className="pr-target-save" onClick={saveTarget}>{t('prTargetSave')}</button>
                  <button type="button" className="pr-target-cancel" onClick={() => setEditTarget(false)}>{t('prTargetCancel')}</button>
                  {ownTarget != null && (
                    <button type="button" className="pr-target-cancel" onClick={clearTarget}>{t('prTargetReset')}</button>
                  )}
                </div>
                <p className="pr-target-hint">{t('prTargetHint')}</p>
              </div>
          ) : (
            <button
              type="button"
              className="pr-target-btn"
              onClick={() => { setTargetDraft(ownTarget ? String(ownTarget) : ''); setEditTarget(true); }}
            >
              {ownTarget != null ? t('prTargetChange') : t('prTargetSet')}
            </button>
          )}
          {rentedRows.length > 0 && (
            <div className="pr-note" style={{ marginTop: 14 }}>
              {firstName ? `${t('prCongrats')}, ${firstName} — ` : ''}
              <b>{rentedRows[0].unit.name || rentedRows[0].unit.project} {t('prIsLeased')}</b>{' '}
              {money(rentedRows[0].annualRent)} ({moneyAlt(rentedRows[0].annualRent)}) {t('prAnnualRentSecured')};{' '}
              {rentedRows.length} {t('prOf')} {completedRows.length}{' '}
              {completedRows.length === 1 ? t('prCompletedUnit') : t('prCompletedUnits')} {t('prNowLeased')}
            </div>
          )}
        </div>

        {/* next payments */}
        {payments.upcoming.length > 0 && (
          <div className="pr-alerts">
            {payments.upcoming.slice(0, 2).map((p, i) => (
              <div key={`${p.unitName}-${p.installmentNumber}`} className={`pr-alert${i === 0 ? ' pr-alert-now' : ''}`}>
                <p className="pr-label">
                  {i === 0 ? t('prNextPayment') : dateLabel(p.dueDate)}
                </p>
                <p className="pr-alert-title">
                  {unitLabel(p.unitName, p.unitNumber)}
                </p>
                <p className="pr-alert-sub">
                  {money(p.amount)} · {moneyAlt(p.amount)}
                  {p.description ? ` (${p.description})` : ''} · {t('prDue')} {dateLabel(p.dueDate)}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* paid vs open */}
        <div className="pr-panel">
          <div className="pr-panel-head">
            <p className="pr-label">{t('prDivisionPaidOpen')}</p>
            <span className="pr-label">
              <b>{money(divisionBase)} · {moneyAlt(divisionBase)}</b>
            </span>
          </div>
          {payments.hasData ? (
            <>
              <div className="pr-bar">
                <span className="pr-bar-fill" style={{ width: `${(paidAED / divisionBase) * 100}%` }} />
                <span
                  className="pr-bar-fill"
                  style={{ width: `${(openAED / divisionBase) * 100}%`, background: 'var(--pr-gold)' }}
                />
              </div>
              <div className="pr-legend">
                <span>
                  <i className="pr-dot" style={{ background: 'var(--pr-ink)' }} />
                  {t('prAlreadyPaid')} — {money(paidAED)} · {moneyAlt(paidAED)} ({pct((paidAED / divisionBase) * 100, 0)})
                </span>
                <span>
                  <i className="pr-dot" style={{ background: 'var(--pr-gold)' }} />
                  {t('prOutstanding')} — {money(openAED)} · {moneyAlt(openAED)}
                </span>
              </div>
            </>
          ) : (
            <p className="pr-hint" style={{ margin: 0 }}>
              {t('prPaymentSplitHint')}
            </p>
          )}
        </div>

        {/* mix */}
        <div className="pr-panel">
          <div className="pr-panel-head">
            <p className="pr-label">{t('prMixByPurchase')}</p>
            <span className="pr-label">
              <b>{money(totalPurchase)} · {moneyAlt(totalPurchase)}</b>
            </span>
          </div>
          <div className="pr-bar">
            <span className="pr-bar-fill" style={{ width: `${(offplanValue / (totalPurchase || 1)) * 100}%` }} />
            <span
              className="pr-bar-fill"
              style={{ width: `${(completedValue / (totalPurchase || 1)) * 100}%`, background: 'var(--pr-gold)' }}
            />
          </div>
          <div className="pr-legend">
            <span>
              <i className="pr-dot" style={{ background: 'var(--pr-ink)' }} />
              {t('prOffPlanCap')} ({offplanRows.length} {t('prObjects')}) — {pct((offplanValue / (totalPurchase || 1)) * 100, 0)} · {money(offplanValue)}
            </span>
            <span>
              <i className="pr-dot" style={{ background: 'var(--pr-gold)' }} />
              {t('prCompletedCap')} ({completedRows.length} {t('prObjects')}) — {pct((completedValue / (totalPurchase || 1)) * 100, 0)} · {money(completedValue)}
            </span>
          </div>
        </div>
      </section>

      {/* ================ Capital deployment ================ */}
      <section className="pr-sec">
        <SectionHead index={nextSec()} title={`${t('prSec2')} ${new Date().getFullYear()}`} />
        <p className="pr-lede pr-serif">
          {money(totalPurchase)} {t('prCommitted')} {moneyE(Math.max(0, capitalTarget - eur(totalPurchase)))} {t('prToGoSentence')}
        </p>

        <div className="pr-panel">
          <div className="pr-panel-head">
            <p className="pr-label">
              {t('prProgressTowardsTarget')} <b>{moneyE(capitalTarget)} ({moneyAltE(capitalTarget)})</b>
            </p>
            <span className="pr-panel-fig pr-serif">{pct(capitalRatio * 100)}</span>
          </div>
          <Bar ratio={capitalRatio} />
          <div className="pr-scale">
            <span>{moneyE(0)}</span>
            <span>{moneyE(capitalTarget * 0.25)}</span>
            <span>{moneyE(capitalTarget * 0.5)}</span>
            <span>{moneyE(capitalTarget * 0.75)}</span>
            <span>{moneyE(capitalTarget)}</span>
          </div>
          <div className="pr-legend">
            <span>
              <i className="pr-dot" style={{ background: 'var(--pr-ink)' }} />
              {t('prInvestedNotarised')} — {money(totalPurchase)} · {moneyAlt(totalPurchase)}
            </span>
            <span>
              <i className="pr-dot" style={{ background: 'var(--pr-track)' }} />
              {t('prAvailable')} — {moneyE(Math.max(0, capitalTarget - eur(totalPurchase)))} ·{' '}
              {moneyAltE(Math.max(0, capitalTarget - eur(totalPurchase)))}
            </span>
          </div>

          <p className="pr-label" style={{ marginTop: 20 }}>{t('prMilestonesCapital')}</p>
          <div className="pr-chips">
            {EUR_LADDER.filter(step => step <= capitalTarget).slice(-5).map(step => (
              <div key={step} className="pr-chip" data-done={eur(totalPurchase) >= step}>
                <div className="pr-chip-val pr-serif">{moneyE(step)}</div>
                <div className="pr-chip-lab">{t('prDeployed')}</div>
                <div className="pr-chip-state">
                  {eur(totalPurchase) >= step ? `✓ ${t('prAchieved')}` : t('prOpenState')}
                </div>
              </div>
            ))}
          </div>

          <p className="pr-label" style={{ marginTop: 20 }}>{t('prMilestonesRent')}</p>
          <div className="pr-chips">
            {RENT_LADDER.filter(step => step <= rentTarget).slice(-5).map(step => (
              <div key={step} className="pr-chip" data-done={eur(annualNetRent) >= step}>
                <div className="pr-chip-val pr-serif">{moneyE(step)}</div>
                <div className="pr-chip-lab">{t('prRentPa')}</div>
                <div className="pr-chip-state">
                  {eur(annualNetRent) >= step ? `✓ ${t('prAchieved')}` : t('prOpenState')}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* live panel */}
        <div className="pr-dark">
          <p className="pr-kicker">
            <span className="pr-live-dot" style={{ display: 'inline-block', marginRight: 7 }} />
            {t('prEarnsLive')}
          </p>
          <p className="pr-live-num pr-serif">{liveMoney(earnedTodayEUR)}</p>
          {/* "Accrued", not "earned/net": this is the pro-rata share of the
              contracted annual rent — gross, before service charges/fees —
              not cash received. Rent arrives as 1–4 cheques a year, so a
              "money received today" reading would be wrong almost every day.
              The basis line below makes the figure auditable for investors. */}
          <p className="pr-live-sub">
            {t('prAccruedToday')} · {rentedRows.length}{' '}
            {rentedRows.length === 1 ? t('prRentedProperty') : t('prRentedProperties')}
          </p>
          <div className="pr-live-grid">
            <div>
              <div className="pr-live-cell-val pr-serif">{moneyE(dailyRentEUR)}</div>
              <div className="pr-live-cell-lab">{t('prPerDay')}</div>
            </div>
            <div>
              <div className="pr-live-cell-val pr-serif">{moneyE(eur(annualNetRent) / 12)}</div>
              <div className="pr-live-cell-lab">{t('prPerMonthAvg')}</div>
            </div>
            <div>
              <div className="pr-live-cell-val pr-serif pr-gold">{moneyE(rentTarget / 365)}</div>
              <div className="pr-live-cell-lab">{t('prPerDayAtPre')} {moneyE(rentTarget)} {t('prPerDayAtPost')}</div>
            </div>
          </div>
          <div className="pr-pills">
            <span className="pr-pill">
              {t('prNextMilestone')}: <b>{moneyE(nextLadder(eur(totalPurchase), EUR_LADDER))} {t('prInvestedWord')}</b> —{' '}
              {moneyE(nextLadder(eur(totalPurchase), EUR_LADDER) - eur(totalPurchase))} {t('prToGoShort')}
            </span>
            <span className="pr-pill">
              {t('prNextRentalTarget')}: <b>{moneyE(nextLadder(eur(annualNetRent), RENT_LADDER))} {t('prPa')}</b> —{' '}
              {moneyE(nextLadder(eur(annualNetRent), RENT_LADDER) - eur(annualNetRent))} {t('prToGoShort')}
            </span>
          </div>
          <p className="pr-live-basis">
            {t('prBasisPre')} {rentedRows.length}{' '}
            {rentedRows.length === 1 ? t('prSignedTenancy') : t('prSignedTenancies')} ({compactAED(annualNetRent)} ={' '}
            {compactEUR(eur(annualNetRent))} {t('prPa')}) ÷ 365 = {liveMoney(dailyRentEUR)} {t('prPerDay')},{' '}
            {t('prBasisAccrued')} {t('prBasisGross')} AED {AED_PER_EUR.toFixed(2)} {t('prPerEur')}.
          </p>
        </div>

        {/* achievements */}
        <div className="pr-panel">
          <div className="pr-panel-head">
            <p className="pr-label">{t('prMilestonesAchievements')}</p>
            <span className="pr-label pr-serif" style={{ fontSize: '0.95rem', color: 'var(--pr-gold)' }}>
              {unlocked} / {badges.length} · {t('prUnlocked')}
            </span>
          </div>
          <div className="pr-badges">
            {badges.map(b => (
              <div key={b.name} className="pr-badge" data-done={b.done}>
                <div className="pr-badge-medal pr-serif">
                  {b.icon}
                  {b.done && <span className="pr-badge-tick">✓</span>}
                </div>
                <div className="pr-badge-name">{b.name}</div>
                <div className="pr-badge-desc">{b.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================ Object register ================ */}
      <section className="pr-sec">
        <SectionHead index={nextSec()} title={t('prSec3')} />
        <p className="pr-lede pr-serif">
          {rows.length} {rows.length === 1 ? t('prUnit') : t('prUnits')}. {t('prOneSkyline')}
        </p>

        {/* pr-reg-list gives each unit its own panel with space around it —
            see the register rules in PortfolioReport.css. */}
        <div className="pr-table pr-reg-list">
          <div className="pr-thead pr-grid-reg-h">
            <span>{t('prColObject')}</span><span>{t('prColUnit')}</span><span>{t('prColType')}</span><span>{t('prColPosition')}</span>
            <span>{t('prColHandover')}</span><span className="pr-num">{t('prColPurchase')} ({primary})</span>
            <span className="pr-num">{primary === 'EUR' ? 'AED' : 'EUR'}</span><span>{t('prColStatus')}</span>
          </div>
          {rows.map(r => {
            const isOpen = openUnit === r.unit.id;
            const gain = r.marketValue - r.purchase;
            return (
              <React.Fragment key={r.unit.id}>
                <button
                  className="pr-tr pr-grid-reg"
                  data-open={isOpen}
                  onClick={() => setOpenUnit(isOpen ? null : r.unit.id)}
                >
                  <div className="pr-c-main">
                    <div className="pr-tr-name">
                      {isOpen
                        ? <ChevronDown size={13} color="#a4844a" />
                        : <ChevronRight size={13} color="#a4844a" />}
                      {r === rows[0] && <span className="pr-diamond">◆</span>}
                      {registerTitle(r.unit)}
                    </div>
                    <div className="pr-tr-sub">
                      {r === rows[0] ? `${t('prBadgeTrophy')} · ` : ''}
                      {r.offplan ? t('prOffPlanLower') : t('prCompletedLower')}
                    </div>
                  </div>
                  <Cell label={t('prColUnit')} cls="pr-reg-unit">
                    <div className="pr-tr-fig" style={{ fontSize: '0.8rem' }}>{r.unit.unitNumber || '—'}</div>
                  </Cell>
                  <Cell label={t('prColType')} cls="pr-reg-type">
                    <div className="pr-tr-sub" style={{ marginTop: 0 }}>
                      {/* Many `type` values already carry the bedroom count
                          ("1 B/R"), which rendered as "1 BR 1 B/R". */}
                      {(() => {
                        const type = String(r.unit.type || '').trim();
                        const beds = r.unit.bedrooms;
                        const typeHasBeds = /b\s*\/?\s*r|bed/i.test(type);
                        if (!type) return beds ? `${beds} BR` : '—';
                        return beds && !typeHasBeds ? `${beds} BR ${type}` : type;
                      })()}
                    </div>
                  </Cell>
                  <Cell label={t('prColPosition')} cls="pr-reg-pos">
                    {/* When the title already IS the project, show the location
                        instead of printing the project twice on one row. */}
                    <div className="pr-tr-sub" style={{ marginTop: 0 }}>
                      {(() => {
                        const title = registerTitle(r.unit);
                        const project = String(r.unit.project || '').trim();
                        const place = String(r.unit.location || '').trim();
                        if (project && project.toLowerCase() !== title.toLowerCase()) return project;
                        return place || project || '—';
                      })()}
                    </div>
                  </Cell>
                  <Cell label={t('prColHandover')} cls="pr-reg-hand">
                    <div className="pr-tr-sub" style={{ marginTop: 0 }}>{r.handoverLabel}</div>
                  </Cell>
                  <Cell label={`${t('prColPurchase')} (${primary})`} num cls="pr-reg-amt">
                    <div className="pr-tr-fig pr-serif">{moneyFull(r.purchase)}</div>
                  </Cell>
                  <Cell label={`${t('prColPurchase')} (${primary === 'EUR' ? 'AED' : 'EUR'})`} num cls="pr-reg-alt">
                    <div className="pr-tr-fig-alt pr-serif">{moneyAltFull(r.purchase)}</div>
                  </Cell>
                  <Cell label={t('prColStatus')} cls="pr-reg-status">
                    <span className="pr-status" data-tone={r.rented ? 'live' : r.offplan ? 'muted' : undefined}>
                      {r.offplan
                        ? t('prOffPlanCap')
                        : r.rented ? t('prFinishedRented') : t('prFinishedVacant')}
                    </span>
                  </Cell>
                </button>

                {isOpen && (
                  <div className="pr-detail">
                    <div className="pr-detail-grid">
                      <div>
                        <p className="pr-label">{t('prArea')}</p>
                        <p className="pr-detail-val pr-serif">
                          {r.area ? `${Math.round(r.area).toLocaleString()} sqft` : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="pr-label">{t('prPricePerSqft')}</p>
                        <p className="pr-detail-val pr-serif">
                          {r.area ? `AED ${fmtAEDFull(r.purchase / r.area)}` : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="pr-label">{t('prPurchased')}</p>
                        <p className="pr-detail-val pr-serif">{dateLabel(r.unit.purchaseDate || r.unit.createdAt)}</p>
                      </div>
                      <div>
                        <p className="pr-label">{t('prColHandover')}</p>
                        <p className="pr-detail-val pr-serif">{dateLabel(r.handoverISO)}</p>
                      </div>
                      <div>
                        <p className="pr-label">
                          {r.hasDldValue ? t('prMarketValueDld') : t('prMarketValue')}
                        </p>
                        <p className="pr-detail-val pr-serif">{moneyFull(r.marketValue)}</p>
                        {!r.hasDldValue && (
                          <p className="pr-tr-fig-alt">{t('prNoDldComps')}</p>
                        )}
                      </div>
                      <div>
                        <p className="pr-label">{t('prUnrealisedGain')}</p>
                        <p className={`pr-detail-val pr-serif ${gain >= 0 ? 'pr-green' : ''}`}>
                          {gain >= 0 ? '+' : ''}{moneyFull(gain)}
                        </p>
                      </div>
                      <div>
                        <p className="pr-label">{t('prRentPerMonth')}</p>
                        <p className="pr-detail-val pr-serif">
                          {r.monthlyRent > 0
                            ? moneyFull(r.monthlyRent)
                            : r.estMonthlyRent > 0 ? `${moneyFull(r.estMonthlyRent)} ${t('prEst')}` : '—'}
                        </p>
                      </div>
                      <div>
                        <p className="pr-label">{t('prGrossYield')}</p>
                        <p className="pr-detail-val pr-serif">
                          {r.annualRent > 0 && r.purchase > 0 ? pct((r.annualRent / r.purchase) * 100) : '—'}
                        </p>
                      </div>
                      {r.lease && (
                        <>
                          <div>
                            <p className="pr-label">{t('prTenant')}</p>
                            <p className="pr-detail-val">{r.lease.tenant || t('prLeased')}</p>
                          </div>
                          <div>
                            <p className="pr-label">{t('prRentCollected')}</p>
                            <p className="pr-detail-val pr-serif pr-green">
                              {moneyFull(r.lease.collected)}
                              <span className="pr-tr-fig-alt"> {t('prOf')} {moneyFull(r.lease.annualRent)}</span>
                            </p>
                          </div>
                          <div>
                            <p className="pr-label">{t('prExpensesActual')}</p>
                            <p className="pr-detail-val pr-serif">
                              {r.lease.expenses > 0 ? moneyFull(r.lease.expenses) : '—'}
                            </p>
                          </div>
                          <div>
                            <p className="pr-label">{t('prNetRent')}</p>
                            <p className="pr-detail-val pr-serif">
                              {moneyFull(Math.max(0, r.lease.annualRent - r.lease.expenses))}
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
          <div className="pr-tr pr-grid-reg pr-tr-total">
            <div className="pr-c-main">
              <div className="pr-tr-name">{t('prInTotal')}</div>
            </div>
            <Cell label={t('prUnits')}>
              <div className="pr-tr-fig pr-serif">{rows.length}</div>
            </Cell>
            <div className="pr-c" /><div className="pr-c" /><div className="pr-c" />
            <Cell label={`${t('prColPurchase')} (${primary})`} num>
              <div className="pr-tr-fig pr-serif">{moneyFull(totalPurchase)}</div>
            </Cell>
            <Cell label={`${t('prColPurchase')} (${primary === 'EUR' ? 'AED' : 'EUR'})`} num>
              <div className="pr-tr-fig-alt pr-serif">{moneyAltFull(totalPurchase)}</div>
            </Cell>
            <div className="pr-c" />
          </div>
        </div>
        <p className="pr-hint">▸ {t('prTapObject')}</p>
      </section>

      {renderRealisedSales()}

      {/* ================ Payments per year ================ */}
      {payments.hasData && (
        <section className="pr-sec">
          <SectionHead index={nextSec()} title={t('prSec4')} />
          <div className="pr-table">
            <div className="pr-thead pr-grid-pay-h">
              <span>{t('prColYear')}</span><span>{t('prColCount')}</span>
              <span className="pr-num">{t('prColAmount')} ({primary})</span>
              <span className="pr-num">{primary === 'EUR' ? 'AED' : 'EUR'}</span>
              <span className="pr-num">{t('prColPaidOfWhich')}</span><span className="pr-num">{t('prColStillOpen')}</span>
            </div>
            {payments.years.map(y => {
              const isOpen = openYear === y.year;
              return (
                <React.Fragment key={y.year}>
                  <button
                    className="pr-tr pr-grid-pay"
                    data-open={isOpen}
                    onClick={() => setOpenYear(isOpen ? null : y.year)}
                  >
                    <div className="pr-c-main">
                      <div className="pr-tr-name">
                        {isOpen
                          ? <ChevronDown size={13} color="#a4844a" />
                          : <ChevronRight size={13} color="#a4844a" />}
                        {y.year}
                      </div>
                    </div>
                    <Cell label={t('prInstallments')} cls="pr-pay-count">
                      <div className="pr-tr-fig" style={{ fontSize: '0.8rem' }}>{y.count}</div>
                    </Cell>
                    <Cell label={`${t('prColAmount')} (${primary})`} num cls="pr-pay-amt">
                      <div className="pr-tr-fig pr-serif">{moneyFull(y.total)}</div>
                    </Cell>
                    <Cell label={`${t('prColAmount')} (${primary === 'EUR' ? 'AED' : 'EUR'})`} num cls="pr-pay-alt">
                      <div className="pr-tr-fig-alt pr-serif">{moneyAltFull(y.total)}</div>
                    </Cell>
                    <Cell label={t('prPaid')} num cls="pr-pay-paid">
                      <div className="pr-tr-fig pr-serif pr-paid">{moneyFull(y.paid)}</div>
                    </Cell>
                    <Cell label={t('prColStillOpen')} num cls="pr-pay-open">
                      <div className="pr-tr-fig pr-serif">{moneyFull(y.open)}</div>
                    </Cell>
                  </button>
                  {isOpen && (
                    <div className="pr-detail">
                      <div className="pr-detail-list">
                        {y.items.map(m => (
                          <div key={`${m.unitName}-${m.installmentNumber}-${m.dueDate}`} className="pr-detail-item">
                            <span>
                              <b>{unitLabel(m.unitName, m.unitNumber)}</b>
                              <br />
                              <span style={{ color: 'var(--pr-muted)', fontSize: '0.68rem' }}>
                                {dateLabel(m.dueDate)} · {rateLabel(m)}
                              </span>
                            </span>
                            <span className={m.status === 'paid' ? 'pr-paid pr-serif' : 'pr-open pr-serif'}>
                              {moneyFull(m.amount)}
                              <br />
                              <span style={{ fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                {m.status}
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            <div className="pr-tr pr-grid-pay pr-tr-total">
              <div className="pr-c-main"><div className="pr-tr-name">{t('prInTotal')}</div></div>
              <Cell label={t('prInstallments')}>
                <div className="pr-tr-fig" style={{ fontSize: '0.8rem' }}>{payments.count}</div>
              </Cell>
              <Cell label={`${t('prColAmount')} (${primary})`} num>
                <div className="pr-tr-fig pr-serif">{moneyFull(payments.total)}</div>
              </Cell>
              <Cell label={`${t('prColAmount')} (${primary === 'EUR' ? 'AED' : 'EUR'})`} num>
                <div className="pr-tr-fig-alt pr-serif">{moneyAltFull(payments.total)}</div>
              </Cell>
              <Cell label={t('prPaid')} num>
                <div className="pr-tr-fig pr-serif pr-paid">{moneyFull(paidAED)}</div>
              </Cell>
              <Cell label={t('prColStillOpen')} num>
                <div className="pr-tr-fig pr-serif">{moneyFull(openAED)}</div>
              </Cell>
            </div>
          </div>
          <p className="pr-hint">▸ {t('prTapYear')}</p>

          {payments.upcoming.length > 0 && (
            <>
              <p className="pr-label" style={{ margin: '20px 2px 8px' }}>{t('prNext5')}</p>
              <div className="pr-table">
                <div className="pr-thead pr-grid-next-h">
                  <span>{t('prColDue')}</span><span>{t('prColObject')}</span><span>{t('prColRate')}</span>
                  <span className="pr-num">{primary}</span>
                  <span className="pr-num">{primary === 'EUR' ? 'AED' : 'EUR'}</span>
                </div>
                {payments.upcoming.slice(0, 5).map(p => (
                  <div key={`${p.unitName}-${p.installmentNumber}-${p.dueDate}`} className="pr-tr pr-grid-next">
                    <div className="pr-c-main pr-next-date">
                      <div className="pr-tr-name" style={{ fontSize: '0.8rem' }}>{dateLabel(p.dueDate)}</div>
                    </div>
                    <Cell label={t('prColObject')} cls="pr-next-obj">
                      <div className="pr-tr-sub" style={{ marginTop: 0 }}>
                        {unitLabel(p.unitName, p.unitNumber)}
                      </div>
                    </Cell>
                    <Cell label={t('prColRate')} cls="pr-next-rate">
                      <div className="pr-tr-sub" style={{ marginTop: 0 }}>{rateLabel(p)}</div>
                    </Cell>
                    <Cell label={primary} num cls="pr-next-amt">
                      <div className="pr-tr-fig pr-serif">{moneyFull(p.amount)}</div>
                    </Cell>
                    <Cell label={primary === 'EUR' ? 'AED' : 'EUR'} num cls="pr-next-alt">
                      <div className="pr-tr-fig-alt pr-serif">{moneyAltFull(p.amount)}</div>
                    </Cell>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* ================ Acquisition pipeline ================ */}
      {pipeline && pipeline.length > 0 && (
        <section className="pr-sec">
          <SectionHead index={nextSec()} title={t('prSec5')} />
          <div className="pr-table">
            {pipeline.map(deal => (
              <div key={deal.name} className="pr-tr" style={{ gridTemplateColumns: '1fr' }}>
                <div className="pr-c-main">
                  <div className="pr-tr-name">{deal.name}</div>
                  {deal.detail && <div className="pr-tr-sub">{deal.detail}</div>}
                </div>
                <div className="pr-detail-grid" style={{ paddingTop: 10 }}>
                  <div>
                    <p className="pr-label">{t('prSegment')}</p>
                    <p className="pr-detail-val">{deal.segment || '—'}</p>
                  </div>
                  <div>
                    <p className="pr-label">{t('prPhase')}</p>
                    <p className="pr-detail-val">
                      <span className="pr-status">{deal.phase || t('prReviewing')}</span>
                    </p>
                  </div>
                  <div>
                    <p className="pr-label">{t('prOffer')}</p>
                    <p className="pr-detail-val pr-serif">{moneyFull(deal.offerAED)}</p>
                    <p className="pr-tr-fig-alt pr-serif">{moneyAltFull(deal.offerAED)}</p>
                  </div>
                  <div>
                    <p className="pr-label">{t('prNetRent')}</p>
                    <p className="pr-detail-val pr-serif">
                      {deal.netRentAED ? moneyFull(deal.netRentAED) : '—'}
                    </p>
                    {!!deal.netReturn && (
                      <p className="pr-tr-fig-alt pr-serif">
                        {trimZeros(deal.netReturn.toFixed(2))}% {t('prNetReturnLower')}
                      </p>
                    )}
                  </div>
                </div>
                {deal.nextSteps && (
                  <p className="pr-tr-sub" style={{ marginTop: 10 }}>
                    <b>{t('prNextSteps')} · {deal.deadline || t('prOpenState')} — </b>{deal.nextSteps}
                  </p>
                )}
              </div>
            ))}
          </div>
          {deploymentPlan && <div className="pr-note">{deploymentPlan}</div>}
        </section>
      )}

      {/* ================ Projection ================ */}
      <section className="pr-sec">
        <SectionHead index={nextSec()} title={`${t('prSec6')} ${projection.year}`} />
        <p className="pr-lede pr-serif">
          {t('prUntilDecember')} {moneyE(projection.peak.annualRent / 365 / AED_PER_EUR)} {t('prRentWord')} {t('prPerDaySentence')}
        </p>

        <div className="pr-kpis">
          <div className="pr-kpi">
            <p className="pr-label">{t('prInvestedByEnd')} {projection.year}</p>
            <p className="pr-kpi-val pr-serif">{money(projection.peak.cumulative)}</p>
            <p className="pr-kpi-alt pr-serif">{moneyAlt(projection.peak.cumulative)}</p>
          </div>
          <div className="pr-kpi">
            <p className="pr-label">{t('prAnnualRentAtEnd')} {projection.year}</p>
            <p className="pr-kpi-val pr-serif pr-green">{money(projection.peak.annualRent)}</p>
            <p className="pr-kpi-alt pr-serif">{moneyAlt(projection.peak.annualRent)}</p>
          </div>
          <div className="pr-kpi">
            <p className="pr-label">{t('prMonthlyRent')}</p>
            <p className="pr-kpi-val pr-serif">{money(projection.peak.monthlyRent)}</p>
            <p className="pr-kpi-alt pr-serif">{moneyAlt(projection.peak.monthlyRent)}</p>
          </div>
          <div className="pr-kpi">
            <p className="pr-label">{t('prDailyRent')}</p>
            <p className="pr-kpi-val pr-serif pr-gold">{money(projection.peak.annualRent / 365)}</p>
            <p className="pr-kpi-alt pr-serif">{moneyAlt(projection.peak.annualRent / 365)}</p>
          </div>
        </div>

        <div className="pr-table">
          <div className="pr-thead pr-grid-proj-h">
            <span>{t('prColMonth')} {projection.year}</span><span className="pr-num">{t('prCapitalInvested')}</span>
            <span className="pr-num">{t('prCumulativeInvestments')}</span>
            <span className="pr-num">{t('prAnnualRent')}</span>
            <span className="pr-num">{t('prMonthlyRent')}</span><span />
          </div>
          {projection.months.map(m => (
            <div key={m.label} className="pr-proj-row pr-grid-proj">
              <div className="pr-c-main">
                <div className="pr-tr-name">{m.label}</div>
              </div>
              <Cell label={t('prCapitalInvested')} num>
                <div className="pr-tr-fig pr-serif">
                  {m.inflow > 0 ? `+${money(m.inflow)}` : '—'}
                </div>
                {m.inflow > 0 && <div className="pr-tr-fig-alt pr-serif">{moneyAlt(m.inflow)}</div>}
              </Cell>
              <Cell label={t('prCumulative')} num>
                <div className="pr-tr-fig pr-serif">{money(m.cumulative)}</div>
                <div className="pr-tr-fig-alt pr-serif">{moneyAlt(m.cumulative)}</div>
              </Cell>
              <Cell label={t('prAnnualRent')} num>
                <div className="pr-tr-fig pr-serif pr-green">{money(m.annualRent)}</div>
                <div className="pr-tr-fig-alt pr-serif">{moneyAlt(m.annualRent)}</div>
              </Cell>
              <Cell label={t('prMonthlyRent')} num>
                <div className="pr-tr-fig-alt pr-serif" style={{ fontSize: '0.8rem' }}>
                  {money(m.monthlyRent)}
                </div>
              </Cell>
              <div className="pr-proj-bar">
                <i style={{ width: `${(m.annualRent / projection.maxRent) * 100}%` }} />
              </div>
            </div>
          ))}
          <p className="pr-foot">
            {t('prBaseline')} {monthName(new Date().getMonth())}: {money(totalPurchase)} {t('prInvestedWord')},{' '}
            {money(annualNetRent)} {t('prFootRest')}
          </p>
        </div>

        {/* long-term */}
        <p className="pr-lede pr-serif" style={{ marginTop: 26 }}>
          {t('prLtPre')} {moneyE(capitalTarget)} {t('prLtMid')} {Math.round(YIELD_CEILING * 100)}%: {t('prLtGoal')} {moneyE(rentTarget)}.
          {longTerm.rows.some(r => r.goal)
            ? ` ${t('prReachedIn')} ${longTerm.rows.find(r => r.goal)!.year}.`
            : ` ${t('prInSight')}`}
        </p>
        <div className="pr-table">
          <div className="pr-thead pr-grid-proj-h">
            <span>{t('prColYear')}</span><span className="pr-num">{t('prColPortfolio')}</span>
            <span className="pr-num">{t('prColNetReturn')}</span><span className="pr-num">{t('prAnnualRent')}</span>
            <span className="pr-num">{t('prMonthlyRent')}</span><span />
          </div>
          {longTerm.rows.map(r => (
            <div key={r.year} className="pr-proj-row pr-grid-proj" data-goal={r.goal}>
              <div className="pr-c-main">
                <div className="pr-tr-name">
                  {r.year} {r.goal && <span className="pr-diamond">✦</span>}
                </div>
              </div>
              <Cell label={t('prColPortfolio')} num>
                <div className="pr-tr-fig pr-serif">{moneyE(r.portfolio)}</div>
                <div className="pr-tr-fig-alt pr-serif">{moneyAltE(r.portfolio)}</div>
              </Cell>
              <Cell label={t('prColNetReturn')} num>
                <div className="pr-tr-fig pr-serif">{pct(r.yieldPct)}</div>
              </Cell>
              <Cell label={t('prAnnualRent')} num>
                <div className="pr-tr-fig pr-serif pr-green">{moneyE(r.annual)}</div>
                <div className="pr-tr-fig-alt pr-serif">{moneyAltE(r.annual)}</div>
              </Cell>
              <Cell label={t('prMonthlyRent')} num>
                <div className="pr-tr-fig-alt pr-serif" style={{ fontSize: '0.8rem' }}>
                  {moneyE(r.monthly)}
                </div>
              </Cell>
              {r.goal ? (
                <div className="pr-c pr-label" style={{ color: 'var(--pr-gold)' }}>✦ {t('prGoalAchieved')}</div>
              ) : (
                <div className="pr-proj-bar">
                  <i style={{ width: `${(r.annual / longTerm.maxAnnual) * 100}%` }} />
                </div>
              )}
            </div>
          ))}
          <p className="pr-foot">
            {t('prAssumesPre')} {moneyE(capitalTarget)} ({moneyAltE(capitalTarget)}) ·{' '}
            {t('prAssumesRises')} {YIELD_STEP * 100} {t('prAssumesPoints')}{' '}
            {Math.round(NEW_CAPITAL_YIELD * 1000) / 10}% {t('prAssumesTo')} {Math.round(YIELD_CEILING * 100)}%{' '}
            {t('prAssumesThrough')} {Math.round(NEW_CAPITAL_YIELD * 1000) / 10}% {t('prAssumesStays')}{' '}
            {moneyE(capitalTarget * NEW_CAPITAL_YIELD)} — {t('prAssumesStep')} {moneyE(rentTarget)} {t('prAssumesEnd')}
          </p>
        </div>
      </section>

      <p className="pr-closing pr-serif">
        ◆ {t('prTagline')} ◆
      </p>
      <p className="pr-hint" style={{ textAlign: 'center' }}>
        {t('prFooterPre')} AED {AED_PER_EUR.toFixed(2)} {t('prFooterPost')}
      </p>
    </div>
  );
};

export default PortfolioReport;
